import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IncidentInterface } from "../../../models/Incident";
import { CACHE_KEY_INCIDENT } from "../../../utils/constants";
import incidentService from "../../../services/incidentService";

interface DeleteIncidentContext {
  previousIncidents: IncidentInterface[];
  deletedIncident: IncidentInterface | undefined;
}

const useDeleteIncident = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the ticket to delete
    DeleteIncidentContext
  >({
    mutationFn: incidentService.Remove, // Remove should take a string ID

    onMutate: (incidentId: string) => {
      const previousIncidents =
        queryClient.getQueryData<IncidentInterface[]>([
          CACHE_KEY_INCIDENT,
          page,
          pageSize,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<IncidentInterface[]>(
        [CACHE_KEY_INCIDENT, page, pageSize],
        previousIncidents.filter((incident) => incident.id !== incidentId) // Use incidentId to filter
      );

      return {
        previousIncidents,
        deletedIncident: previousIncidents.find(
          (incident) => incident.id === incidentId
        ),
      };
    },

    onError: (error, incidentId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<IncidentInterface[]>(
        [CACHE_KEY_INCIDENT, page, pageSize],
        context.previousIncidents
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteIncident;
